import { useTranslation } from "react-i18next";
import type { ChurchEvent } from "../../model/launchpad";
import { eventTimeParts, relativeDescription } from "../../util/date";
import Card from "../ui/Card";
import BorderedButton from "../ui/BorderedButton";
import EventArtworkHeader from "./EventArtworkHeader";

// Web port of EventCard + FeaturedEventCard
// (EventsFeature/View/Component/EventCard.swift and EventsHomeSectionView.swift).
//
// Artwork with the title over it, and the date plus an RSVP button underneath.
// Kept presentational the way the app splits EventCard from FeaturedEventCard,
// so callers own where a tap goes.

/** EventCard: .frame(height: 230) on the artwork header. */
const ARTWORK_HEIGHT = "h-[230px]";

export interface EventCardProps {
  event: ChurchEvent;
  onClick?: () => void;
}

export default function EventCard(props: EventCardProps) {
  const { t, i18n } = useTranslation("events");
  const { attributes } = props.event;

  const parts = eventTimeParts(
    attributes.startsAt,
    attributes.endsAt,
    attributes.showTimes,
    i18n.language
  );

  // Event.formattedTimeString: a single-day event collapses to its date plus
  // start time; a multi-day span shows both dates and only the start time.
  const startLabel = parts.time
    ? t("dateAtTime", { date: parts.date, time: parts.time })
    : parts.date;
  const timeLabel = parts.endDate
    ? t("dateRange", { start: startLabel, end: parts.endDate })
    : startLabel;

  return <Card applyPadding={false} className="relative isolate">
    <div className={`${ARTWORK_HEIGHT} w-full`}>
      <EventArtworkHeader
        url={attributes.thumbnailURL}
        eyebrow={t("featuredEvent")}
        title={attributes.title}
        description={attributes.description}
      />
    </div>

    <div className="flex items-center gap-3 px-4 pt-4 pb-4">
      <div className="flex min-w-0 flex-col text-left">
        <span className="truncate text-[17px] font-semibold text-gray-900">
          {timeLabel}
        </span>
        <span className="truncate text-[15px] text-gray-500">
          {relativeDescription(attributes.startsAt, i18n.language)}
        </span>
      </div>

      <div className="flex-1" />

      {/* EventRSVPButton renders nothing when there is no link. */}
      {attributes.registrationURL && (
        <span className="relative z-10">
          <BorderedButton href={attributes.registrationURL}>
            {t("rsvp")}
          </BorderedButton>
        </span>
      )}
    </div>

    {/* The whole card opens the event, matching FeaturedEventCard's Button.
        Positioned so it paints over the static content but below the RSVP
        button, which sits at z-10. */}
    {props.onClick && (
      <button
        type="button"
        onClick={props.onClick}
        aria-label={attributes.title}
        className="absolute inset-0 z-0 cursor-pointer"
      />
    )}
  </Card>
}
